import { type ComponentType } from 'react';
import {
  LayoutDashboard,
  Package,
  Clock,
  Tag,
  FolderTree,
  Disc,
  Camera,
  Building2,
  Sparkles,
  Bot,
  BrainCog,
  Webhook,
  ScrollText,
  Users,
  ShieldCheck,
  Terminal,
  Database,
  Inbox,
  Send,
  Upload,
  Blocks,
  ToggleRight,
  type LucideProps,
} from 'lucide-react';
import { APP_MODULES, type AppModuleKey } from '@/config/modules';
import { meetsRequirements } from '@/lib/permissions';

// Estructura del menú lateral: grupos, items y su orden. Vive fuera de
// `Sidebar.tsx` porque también la lee `useHomeTarget`, que necesita la
// primera sección EN EL ORDEN QUE EL USER VE para saber a dónde mandar `/`.

// Item del menú lateral.
export interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<LucideProps>;
  // Módulo del registro (`src/config/modules.ts`): de ahí salen ruta, label y
  // permisos requeridos, los mismos que chequea el guard de la ruta. Ausente =
  // visible para todo user autenticado (ej: Dashboard).
  module?: AppModuleKey;
  // Marker para tour onboarding (data-tour="sidebar-<X>"). Opcional.
  dataTour?: string;
}

export interface NavGroup {
  label: string;
  items: readonly NavItem[];
}

function moduleItem(
  module: AppModuleKey,
  icon: ComponentType<LucideProps>,
  dataTour?: string,
): NavItem {
  const { path, label } = APP_MODULES[module];
  return { to: path, label, icon, module, dataTour };
}

// Módulos agrupados por lo que la persona VA A HACER, no por dominio técnico:
// trabajar el catálogo, ver a quién le entregamos datos, configurar, lanzar un
// proceso, o mirar registros. Antes "Sistema" mezclaba configurar (IA) con
// reprocesar (backfills, reindex) e importar, y los registros estaban repartidos
// entre "Integración B2B", "Auditoría" y "Desarrollo": quien configuraba
// terminaba yendo y volviendo por el menú para encontrar la pantalla vecina.
//
// Cada grupo rinde con un header en uppercase (expanded) o separator
// (collapsed). Reordenar o agregar items acá evita tocar la UI.
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    // Primero porque responde "qué tengo para hacer ahora": el dashboard da
    // los números y Pendientes la lista de lo que falta completar.
    label: 'Resumen',
    items: [
      { to: '/', label: 'Inicio', icon: LayoutDashboard, dataTour: 'sidebar-dashboard' },
      moduleItem('pending', Clock, 'sidebar-pending'),
    ],
  },
  {
    label: 'Catálogo',
    items: [
      moduleItem('products', Package, 'sidebar-products'),
      moduleItem('brands', Tag),
      moduleItem('categories', FolderTree),
      moduleItem('aiSuggestions', Sparkles, 'sidebar-ai-suggestions'),
      moduleItem('vehicleModels', Disc),
      // Temporal: estudio de fotos. Se va con `src/features/photo-studio/`.
      moduleItem('photoStudio', Camera),
    ],
  },
  {
    // Cosas que se lanzan y hay que vigilar mientras corren. Antes que
    // Configuración: se usan seguido, y configurar es de una vez.
    label: 'Procesos',
    items: [
      moduleItem('productImports', Upload, 'sidebar-imports'),
      moduleItem('ragReindex', Database),
      moduleItem('webhookBackfills', Send, 'sidebar-webhook-backfills'),
    ],
  },
  {
    // El integrador se arma una vez (slug, embed, roles con acceso) y después
    // se consulta: es configuración, no operación.
    label: 'Configuración',
    items: [
      // `BrainCog` (cerebro con engranaje): `Settings2` era el icono genérico
      // de ajustes y no decía que esto configura la IA.
      moduleItem('aiConfigs', BrainCog),
      // `Bot` y no `Sparkles`: ese ya lo usa Sugerencias IA y, con el menu
      // colapsado, los dos items quedaban indistinguibles.
      moduleItem('aiModels', Bot),
      moduleItem('integrators', Blocks),
      moduleItem('activationRules', ToggleRight),
    ],
  },
  {
    // Todo lo que es mirar qué pasó, en un solo lugar. Query Lab NO entra acá:
    // no registra nada, es una herramienta para consultar el catálogo a mano.
    label: 'Registros',
    items: [
      moduleItem('auditLogs', ScrollText),
      moduleItem('ingestEvents', Inbox, 'sidebar-ingest-events'),
      moduleItem('webhookDeliveries', Webhook),
    ],
  },
  {
    // Quién entra y con qué: personas (usuarios, roles) y clientes B2B, que
    // son credencial más permisos igual que un usuario, solo que por API.
    label: 'Accesos',
    items: [
      moduleItem('roles', ShieldCheck),
      moduleItem('users', Users),
      moduleItem('adminClients', Building2),
    ],
  },
  {
    label: 'Desarrollo',
    items: [moduleItem('queryLab', Terminal)],
  },
];

// Devuelve grupos con items filtrados por los permisos del módulo.
//
// Gate:
// - Sin user → nada.
// - Item con `module` → visible solo si cumple TODOS sus `requires`.
// - `permissions` undefined (login antes de getMe) → todo visible, igual que
//   el guard; `[]` → solo items sin módulo.
// - Inicio (`/`) → solo si `showHome`. Lo decide `useHomeTarget`: sin
//   ninguna tarjeta de acción en el dashboard, Inicio no se ofrece.
export function filterNavGroups(
  hasUser: boolean,
  permissions: readonly string[] | undefined,
  showHome: boolean,
): readonly NavGroup[] {
  if (!hasUser) return [];
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      if (item.to === '/') return showHome;
      return !item.module || meetsRequirements(permissions, APP_MODULES[item.module].requires);
    }),
  })).filter((group) => group.items.length > 0);
}

// Primera sección (item con módulo) que el user puede abrir, en el orden del
// menú. null si no puede abrir ninguna.
export function firstNavSection(
  hasUser: boolean,
  permissions: readonly string[] | undefined,
): NavItem | null {
  for (const group of filterNavGroups(hasUser, permissions, false)) {
    const item = group.items.find((i) => i.module !== undefined);
    if (item) return item;
  }
  return null;
}
